import { defineComponent as _defineComponent } from 'vue';
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "dataset-summary"
};
const _hoisted_2 = { class: "uri-text text-body-2" };
const _hoisted_3 = { class: "d-flex justify-end ga-2" };
const _hoisted_4 = {
    key: 0,
    class: "d-flex flex-wrap ga-2"
};
const _hoisted_5 = {
    key: 1,
    class: "text-body-2 text-medium-emphasis"
};
const _hoisted_6 = { class: "d-flex justify-end ga-2" };
const _hoisted_7 = { key: 0 };
const _hoisted_8 = { class: "d-flex justify-end ga-2" };
const _hoisted_9 = {
    key: 1,
    class: "text-body-2 text-medium-emphasis"
};
import { ref, computed } from "vue";
import { useStore } from "@/store";
import { useNotificationStore } from "@/stores/notifications";
import { dserverApi } from "@/services/dserverApi";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DatasetSummary',
    setup(__props) {
        const store = useStore();
        const notifications = useNotificationStore();
        // Form state
        const newTagName = ref("");
        const addTagMenu = ref(false);
        const newAnnotationKey = ref("");
        const newAnnotationValue = ref("");
        const addAnnotationMenu = ref(false);
        const editingAnnotationKey = ref(null);
        const editAnnotationValue = ref("");
        const saving = ref(false);
        // Computed properties
        const dataset = computed(() => {
            return store.current_dataset;
        });
        const displayUri = computed(() => {
            if (!dataset.value)
                return "";
            if (store.hasSignedUrlPlugin) {
                const lookupUrl = store.lookup_url;
                if (lookupUrl) {
                    try {
                        const url = new URL(lookupUrl);
                        return `dserver://${url.host}/${dataset.value.uuid}`;
                    }
                    catch {
                        return dataset.value.uri;
                    }
                }
            }
            return dataset.value.uri;
        });
        const copy_command = computed(() => {
            if (!dataset.value)
                return "";
            return "dtool cp " + displayUri.value + " .";
        });
        const currentTags = computed(() => {
            if (store.current_dataset_tags && store.current_dataset_tags.tags) {
                return store.current_dataset_tags.tags;
            }
            return [];
        });
        const currentAnnotations = computed(() => {
            if (store.current_dataset_annotations &&
                store.current_dataset_annotations.annotations) {
                return store.current_dataset_annotations.annotations;
            }
            return {};
        });
        const annotationEntries = computed(() => {
            return Object.entries(currentAnnotations.value);
        });
        function formatAnnotationValue(value) {
            if (value === null || value === undefined)
                return "";
            if (typeof value === "object")
                return JSON.stringify(value);
            return String(value);
        }
        // Annotation edit menu state (tracked per key so editors don't share text)
        function toggleEditMenu(key, open) {
            if (open) {
                editingAnnotationKey.value = key;
                editAnnotationValue.value = "";
            }
            else if (editingAnnotationKey.value === key) {
                editingAnnotationKey.value = null;
                editAnnotationValue.value = "";
            }
        }
        function cancelEditAnnotation() {
            editingAnnotationKey.value = null;
            editAnnotationValue.value = "";
        }
        async function copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                notifications.success("Copied to clipboard");
            }
            catch (err) {
                console.error("Failed to copy:", err);
                notifications.error("Failed to copy to clipboard");
            }
        }
        // Tag operations
        async function addTag() {
            const tagToAdd = newTagName.value?.trim();
            if (!tagToAdd || !dataset.value)
                return;
            if (currentTags.value.includes(tagToAdd)) {
                notifications.warning("Tag already exists");
                return;
            }
            const uri = dataset.value.uri;
            saving.value = true;
            try {
                const result = await dserverApi.addTag(uri, tagToAdd);
                if (store.current_dataset?.uri !== uri)
                    return;
                store.updateCurrentDatasetTags(result);
                newTagName.value = "";
                addTagMenu.value = false;
                notifications.success(`Tag "${tagToAdd}" added`);
            }
            catch (error) {
                console.error("Failed to add tag:", error);
                const err = error;
                if (err.status === 403) {
                    notifications.error("Permission denied. You may not have write access.");
                }
                else {
                    notifications.error("Failed to add tag");
                }
            }
            finally {
                saving.value = false;
            }
        }
        async function removeTag(tag) {
            if (!dataset.value)
                return;
            const uri = dataset.value.uri;
            saving.value = true;
            try {
                const result = await dserverApi.removeTag(uri, tag);
                if (store.current_dataset?.uri !== uri)
                    return;
                store.updateCurrentDatasetTags(result);
                notifications.success(`Tag "${tag}" removed`);
            }
            catch (error) {
                console.error("Failed to remove tag:", error);
                const err = error;
                if (err.status === 403) {
                    notifications.error("Permission denied. You may not have write access.");
                }
                else {
                    notifications.error("Failed to remove tag");
                }
            }
            finally {
                saving.value = false;
            }
        }
        // Annotation operations
        async function addAnnotation() {
            const key = newAnnotationKey.value?.trim();
            if (!key || !dataset.value)
                return;
            // Parse value - try JSON first, then use as string
            let value = newAnnotationValue.value;
            try {
                value = JSON.parse(newAnnotationValue.value);
            }
            catch {
                // Keep as string
            }
            const uri = dataset.value.uri;
            saving.value = true;
            try {
                const result = await dserverApi.setAnnotation(uri, key, value);
                if (store.current_dataset?.uri !== uri)
                    return;
                store.updateCurrentDatasetAnnotations(result);
                newAnnotationKey.value = "";
                newAnnotationValue.value = "";
                addAnnotationMenu.value = false;
                notifications.success(`Annotation "${key}" added`);
            }
            catch (error) {
                console.error("Failed to add annotation:", error);
                const err = error;
                if (err.status === 403) {
                    notifications.error("Permission denied. You may not have write access.");
                }
                else {
                    notifications.error("Failed to add annotation");
                }
            }
            finally {
                saving.value = false;
            }
        }
        async function updateAnnotation(key) {
            // Only save when this key's editor is open; an empty string is a valid value.
            if (!dataset.value || editingAnnotationKey.value !== key)
                return;
            // Parse value - try JSON first, then use as string
            let value = editAnnotationValue.value;
            try {
                value = JSON.parse(editAnnotationValue.value);
            }
            catch {
                // Keep as string
            }
            const uri = dataset.value.uri;
            saving.value = true;
            try {
                const result = await dserverApi.setAnnotation(uri, key, value);
                if (store.current_dataset?.uri !== uri)
                    return;
                store.updateCurrentDatasetAnnotations(result);
                cancelEditAnnotation();
                notifications.success(`Annotation "${key}" updated`);
            }
            catch (error) {
                console.error("Failed to update annotation:", error);
                const err = error;
                if (err.status === 403) {
                    notifications.error("Permission denied. You may not have write access.");
                }
                else {
                    notifications.error("Failed to update annotation");
                }
            }
            finally {
                saving.value = false;
            }
        }
        async function deleteAnnotation(key) {
            if (!dataset.value)
                return;
            const uri = dataset.value.uri;
            saving.value = true;
            try {
                const result = await dserverApi.deleteAnnotation(uri, key);
                if (store.current_dataset?.uri !== uri)
                    return;
                store.updateCurrentDatasetAnnotations(result);
                notifications.success(`Annotation "${key}" deleted`);
            }
            catch (error) {
                console.error("Failed to delete annotation:", error);
                const err = error;
                if (err.status === 403) {
                    notifications.error("Permission denied. You may not have write access.");
                }
                else {
                    notifications.error("Failed to delete annotation");
                }
            }
            finally {
                saving.value = false;
            }
        }
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_menu = _resolveComponent("v-menu");
            const _component_v_card_item = _resolveComponent("v-card-item");
            const _component_v_chip = _resolveComponent("v-chip");
            const _component_v_avatar = _resolveComponent("v-avatar");
            const _component_v_list_item_title = _resolveComponent("v-list-item-title");
            const _component_v_list_item_subtitle = _resolveComponent("v-list-item-subtitle");
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_list = _resolveComponent("v-list");
            return (dataset.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createVNode(_component_v_card, {
                        variant: "outlined",
                        class: "mb-4",
                        rounded: "lg"
                    }, {
                        default: _withCtx(() => [
                            _createVNode(_component_v_card_item, { density: "compact" }, {
                                prepend: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                        size: "small",
                                        color: "primary"
                                    }, {
                                        default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                                _createTextVNode("mdi-link-variant", -1)
                                            ]))]),
                                        _: 1
                                    })
                                ]),
                                append: _withCtx(() => [
                                    _createVNode(_component_v_menu, {
                                        location: "bottom end",
                                        "close-on-content-click": false
                                    }, {
                                        activator: _withCtx(({ props }) => [
                                            _createVNode(_component_v_btn, _mergeProps(props, {
                                                size: "small",
                                                variant: "tonal",
                                                color: "primary",
                                                "prepend-icon": "mdi-content-copy"
                                            }), {
                                                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                                        _createTextVNode(" Copy ", -1)
                                                    ]))]),
                                                _: 1
                                            }, 16)
                                        ]),
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_card, {
                                                "min-width": "360",
                                                rounded: "lg"
                                            }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_card_text, { class: "text-body-2 pb-2" }, {
                                                        default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                                                _createTextVNode(" Copy this dataset to your working directory: ", -1)
                                                            ]))]),
                                                        _: 1
                                                    }),
                                                    _createVNode(_component_v_card_text, { class: "pt-0" }, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_v_text_field, {
                                                                "model-value": copy_command.value,
                                                                readonly: "",
                                                                density: "compact",
                                                                variant: "outlined",
                                                                "hide-details": "",
                                                                rounded: "lg",
                                                                "bg-color": "grey-lighten-4"
                                                            }, {
                                                                "append-inner": _withCtx(() => [
                                                                    _createVNode(_component_v_btn, {
                                                                        icon: "mdi-content-copy",
                                                                        size: "small",
                                                                        variant: "text",
                                                                        onClick: _cache[0] || (_cache[0] = ($event) => (copyToClipboard(copy_command.value)))
                                                                    })
                                                                ]),
                                                                _: 1
                                                            }, 8, ["model-value"])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    })
                                ]),
                                default: _withCtx(() => [
                                    _createVNode(_component_v_card_title, { class: "text-body-2 font-weight-medium" }, {
                                        default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                                _createTextVNode("URI", -1)
                                            ]))]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_text, { class: "pt-0" }, {
                                default: _withCtx(() => [
                                    _createElementVNode("code", _hoisted_2, _toDisplayString(displayUri.value), 1)
                                ]),
                                _: 1
                            })
                        ]),
                        _: 1
                    }),
                    _createVNode(_component_v_card, {
                        variant: "outlined",
                        class: "mb-4",
                        rounded: "lg"
                    }, {
                        default: _withCtx(() => [
                            _createVNode(_component_v_card_item, { density: "compact" }, {
                                prepend: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                        size: "small",
                                        color: "primary"
                                    }, {
                                        default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                                _createTextVNode("mdi-tag-multiple", -1)
                                            ]))]),
                                        _: 1
                                    })
                                ]),
                                append: _withCtx(() => [
                                    _createVNode(_component_v_menu, {
                                        modelValue: addTagMenu.value,
                                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((addTagMenu).value = $event)),
                                        location: "bottom end",
                                        "close-on-content-click": false
                                    }, {
                                        activator: _withCtx(({ props }) => [
                                            _createVNode(_component_v_btn, _mergeProps(props, {
                                                size: "small",
                                                variant: "tonal",
                                                color: "primary",
                                                "prepend-icon": "mdi-tag-plus"
                                            }), {
                                                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                                        _createTextVNode(" Add Tag ", -1)
                                                    ]))]),
                                                _: 1
                                            }, 16)
                                        ]),
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_card, {
                                                "min-width": "300",
                                                rounded: "lg"
                                            }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_card_text, { class: "text-body-2 pb-2" }, {
                                                        default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                                                _createTextVNode(" Add a tag to this dataset: ", -1)
                                                            ]))]),
                                                        _: 1
                                                    }),
                                                    _createVNode(_component_v_card_text, { class: "pt-0" }, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_v_text_field, {
                                                                modelValue: newTagName.value,
                                                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((newTagName).value = $event)),
                                                                label: "Tag name",
                                                                density: "compact",
                                                                variant: "outlined",
                                                                "hide-details": "",
                                                                rounded: "lg",
                                                                class: "mb-3",
                                                                disabled: saving.value,
                                                                onKeyup: _withKeys(addTag, ["enter"])
                                                            }, null, 8, ["modelValue", "disabled"]),
                                                            _createElementVNode("div", _hoisted_3, [
                                                                _createVNode(_component_v_btn, {
                                                                    size: "small",
                                                                    variant: "text",
                                                                    onClick: _cache[2] || (_cache[2] = ($event) => (addTagMenu.value = false))
                                                                }, {
                                                                    default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                                                            _createTextVNode(" Cancel ", -1)
                                                                        ]))]),
                                                                    _: 1
                                                                }),
                                                                _createVNode(_component_v_btn, {
                                                                    size: "small",
                                                                    variant: "tonal",
                                                                    color: "primary",
                                                                    loading: saving.value,
                                                                    disabled: !newTagName.value?.trim(),
                                                                    onClick: addTag
                                                                }, {
                                                                    default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                                                            _createTextVNode(" Add ", -1)
                                                                        ]))]),
                                                                    _: 1
                                                                }, 8, ["loading", "disabled"])
                                                            ])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }, 8, ["modelValue"])
                                ]),
                                default: _withCtx(() => [
                                    _createVNode(_component_v_card_title, { class: "text-body-2 font-weight-medium" }, {
                                        default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                                _createTextVNode("Tags", -1)
                                            ]))]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_text, { class: "pt-0" }, {
                                default: _withCtx(() => [
                                    (currentTags.value.length > 0)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentTags.value, (tag, index) => {
                                                return (_openBlock(), _createBlock(_component_v_chip, {
                                                    key: index,
                                                    variant: "tonal",
                                                    color: "primary",
                                                    size: "small",
                                                    label: "",
                                                    closable: "",
                                                    disabled: saving.value,
                                                    "onClick:close": ($event) => (removeTag(tag))
                                                }, {
                                                    default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(tag), 1)
                                                    ]),
                                                    _: 2
                                                }, 1032, ["disabled", "onClick:close"]));
                                            }), 128))
                                        ]))
                                        : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                            _createVNode(_component_v_icon, {
                                                size: "small",
                                                class: "mr-1"
                                            }, {
                                                default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                                                        _createTextVNode("mdi-information-outline", -1)
                                                    ]))]),
                                                _: 1
                                            }),
                                            _cache[20] || (_cache[20] = _createTextVNode(" No tags yet. Add tags to organize your datasets. ", -1))
                                        ]))
                                ]),
                                _: 1
                            })
                        ]),
                        _: 1
                    }),
                    _createVNode(_component_v_card, {
                        variant: "outlined",
                        rounded: "lg"
                    }, {
                        default: _withCtx(() => [
                            _createVNode(_component_v_card_item, { density: "compact" }, {
                                prepend: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                        size: "small",
                                        color: "primary"
                                    }, {
                                        default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                                _createTextVNode("mdi-tag-text", -1)
                                            ]))]),
                                        _: 1
                                    })
                                ]),
                                append: _withCtx(() => [
                                    _createVNode(_component_v_menu, {
                                        modelValue: addAnnotationMenu.value,
                                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((addAnnotationMenu).value = $event)),
                                        location: "bottom end",
                                        "close-on-content-click": false
                                    }, {
                                        activator: _withCtx(({ props }) => [
                                            _createVNode(_component_v_btn, _mergeProps(props, {
                                                size: "small",
                                                variant: "tonal",
                                                color: "primary",
                                                "prepend-icon": "mdi-plus"
                                            }), {
                                                default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                                                        _createTextVNode(" Add Annotation ", -1)
                                                    ]))]),
                                                _: 1
                                            }, 16)
                                        ]),
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_card, {
                                                "min-width": "320",
                                                rounded: "lg"
                                            }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_card_text, { class: "text-body-2 pb-2" }, {
                                                        default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                                                                _createTextVNode(" Add an annotation to this dataset: ", -1)
                                                            ]))]),
                                                        _: 1
                                                    }),
                                                    _createVNode(_component_v_card_text, { class: "pt-0" }, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_v_text_field, {
                                                                modelValue: newAnnotationKey.value,
                                                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((newAnnotationKey).value = $event)),
                                                                label: "Key",
                                                                density: "compact",
                                                                variant: "outlined",
                                                                "hide-details": "",
                                                                rounded: "lg",
                                                                class: "mb-3",
                                                                disabled: saving.value
                                                            }, null, 8, ["modelValue", "disabled"]),
                                                            _createVNode(_component_v_text_field, {
                                                                modelValue: newAnnotationValue.value,
                                                                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((newAnnotationValue).value = $event)),
                                                                label: "Value",
                                                                density: "compact",
                                                                variant: "outlined",
                                                                "hide-details": "",
                                                                rounded: "lg",
                                                                class: "mb-3",
                                                                disabled: saving.value,
                                                                onKeyup: _withKeys(addAnnotation, ["enter"])
                                                            }, null, 8, ["modelValue", "disabled"]),
                                                            _createElementVNode("div", _hoisted_6, [
                                                                _createVNode(_component_v_btn, {
                                                                    size: "small",
                                                                    variant: "text",
                                                                    onClick: _cache[6] || (_cache[6] = ($event) => (addAnnotationMenu.value = false))
                                                                }, {
                                                                    default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                                                                            _createTextVNode(" Cancel ", -1)
                                                                        ]))]),
                                                                    _: 1
                                                                }),
                                                                _createVNode(_component_v_btn, {
                                                                    size: "small",
                                                                    variant: "tonal",
                                                                    color: "primary",
                                                                    loading: saving.value,
                                                                    disabled: !newAnnotationKey.value?.trim(),
                                                                    onClick: addAnnotation
                                                                }, {
                                                                    default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                                                                            _createTextVNode(" Add ", -1)
                                                                        ]))]),
                                                                    _: 1
                                                                }, 8, ["loading", "disabled"])
                                                            ])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    }, 8, ["modelValue"])
                                ]),
                                default: _withCtx(() => [
                                    _createVNode(_component_v_card_title, { class: "text-body-2 font-weight-medium" }, {
                                        default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                                                _createTextVNode("Annotations", -1)
                                            ]))]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_text, { class: "pt-0" }, {
                                default: _withCtx(() => [
                                    (annotationEntries.value.length > 0)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                            _createVNode(_component_v_list, {
                                                density: "compact",
                                                class: "annotation-list pa-0",
                                                "bg-color": "transparent"
                                            }, {
                                                default: _withCtx(() => [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(annotationEntries.value, ([key, value], index) => {
                                                        return (_openBlock(), _createBlock(_component_v_list_item, {
                                                            key: index,
                                                            rounded: "lg",
                                                            class: "annotation-item mb-1 px-2"
                                                        }, {
                                                            prepend: _withCtx(() => [
                                                                _createVNode(_component_v_avatar, {
                                                                    size: "28",
                                                                    color: "grey-lighten-3",
                                                                    rounded: "lg",
                                                                    class: "mr-2"
                                                                }, {
                                                                    default: _withCtx(() => [
                                                                        _createVNode(_component_v_icon, {
                                                                            size: "12",
                                                                            color: "grey-darken-1"
                                                                        }, {
                                                                            default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                                                                                    _createTextVNode("mdi-key", -1)
                                                                                ]))]),
                                                                            _: 1
                                                                        })
                                                                    ]),
                                                                    _: 1
                                                                })
                                                            ]),
                                                            append: _withCtx(() => [
                                                                _createVNode(_component_v_menu, {
                                                                    "model-value": editingAnnotationKey.value === key,
                                                                    location: "start",
                                                                    "close-on-content-click": false,
                                                                    "onUpdate:modelValue": ($event) => (toggleEditMenu(key, $event))
                                                                }, {
                                                                    activator: _withCtx(({ props }) => [
                                                                        _createVNode(_component_v_btn, _mergeProps({ ref_for: true }, props, {
                                                                            size: "x-small",
                                                                            variant: "text",
                                                                            color: "primary",
                                                                            icon: "mdi-pencil",
                                                                            class: "mr-1",
                                                                            disabled: saving.value
                                                                        }), null, 16, ["disabled"])
                                                                    ]),
                                                                    default: _withCtx(() => [
                                                                        _createVNode(_component_v_card, {
                                                                            "min-width": "300",
                                                                            rounded: "lg"
                                                                        }, {
                                                                            default: _withCtx(() => [
                                                                                _createVNode(_component_v_card_text, { class: "text-body-2 pb-2" }, {
                                                                                    default: _withCtx(() => [
                                                                                        _cache[28] || (_cache[28] = _createTextVNode(" Update value for ", -1)),
                                                                                        _createElementVNode("strong", null, _toDisplayString(key), 1),
                                                                                        _cache[29] || (_cache[29] = _createTextVNode(": ", -1))
                                                                                    ]),
                                                                                    _: 2
                                                                                }, 1024),
                                                                                _createVNode(_component_v_card_text, { class: "pt-0" }, {
                                                                                    default: _withCtx(() => [
                                                                                        _createVNode(_component_v_text_field, {
                                                                                            modelValue: editAnnotationValue.value,
                                                                                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((editAnnotationValue).value = $event)),
                                                                                            label: key,
                                                                                            placeholder: String(value),
                                                                                            density: "compact",
                                                                                            variant: "outlined",
                                                                                            "hide-details": "",
                                                                                            rounded: "lg",
                                                                                            class: "mb-3",
                                                                                            disabled: saving.value,
                                                                                            onKeyup: _withKeys(($event) => (updateAnnotation(key)), ["enter"])
                                                                                        }, null, 8, ["modelValue", "label", "placeholder", "disabled", "onKeyup"]),
                                                                                        _createElementVNode("div", _hoisted_8, [
                                                                                            _createVNode(_component_v_btn, {
                                                                                                size: "small",
                                                                                                variant: "text",
                                                                                                onClick: cancelEditAnnotation
                                                                                            }, {
                                                                                                default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                                                                                                        _createTextVNode(" Cancel ", -1)
                                                                                                    ]))]),
                                                                                                _: 1
                                                                                            }),
                                                                                            _createVNode(_component_v_btn, {
                                                                                                size: "small",
                                                                                                variant: "tonal",
                                                                                                color: "primary",
                                                                                                loading: saving.value,
                                                                                                onClick: ($event) => (updateAnnotation(key))
                                                                                            }, {
                                                                                                default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                                                                                                        _createTextVNode(" Update ", -1)
                                                                                                    ]))]),
                                                                                                _: 1
                                                                                            }, 8, ["loading", "onClick"])
                                                                                        ])
                                                                                    ]),
                                                                                    _: 2
                                                                                }, 1024)
                                                                            ]),
                                                                            _: 2
                                                                        }, 1024)
                                                                    ]),
                                                                    _: 2
                                                                }, 1032, ["model-value", "onUpdate:modelValue"]),
                                                                _createVNode(_component_v_btn, {
                                                                    size: "x-small",
                                                                    variant: "text",
                                                                    color: "error",
                                                                    icon: "mdi-delete",
                                                                    disabled: saving.value,
                                                                    onClick: ($event) => (deleteAnnotation(key))
                                                                }, null, 8, ["disabled", "onClick"])
                                                            ]),
                                                            default: _withCtx(() => [
                                                                _createVNode(_component_v_list_item_title, { class: "text-body-2 font-weight-medium" }, {
                                                                    default: _withCtx(() => [
                                                                        _createTextVNode(_toDisplayString(key), 1)
                                                                    ]),
                                                                    _: 2
                                                                }, 1024),
                                                                _createVNode(_component_v_list_item_subtitle, { class: "text-body-2 text-medium-emphasis" }, {
                                                                    default: _withCtx(() => [
                                                                        _createTextVNode(_toDisplayString(formatAnnotationValue(value)), 1)
                                                                    ]),
                                                                    _: 2
                                                                }, 1024)
                                                            ]),
                                                            _: 2
                                                        }, 1024));
                                                    }), 128))
                                                ]),
                                                _: 1
                                            })
                                        ]))
                                        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                            _createVNode(_component_v_icon, {
                                                size: "small",
                                                class: "mr-1"
                                            }, {
                                                default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                                                        _createTextVNode("mdi-information-outline", -1)
                                                    ]))]),
                                                _: 1
                                            }),
                                            _cache[33] || (_cache[33] = _createTextVNode(" No annotations yet. Add key-value annotations for metadata. ", -1))
                                        ]))
                                ]),
                                _: 1
                            })
                        ]),
                        _: 1
                    })
                ]))
                : _createCommentVNode("", true);
        };
    }
});
